import {Divider, MenuItem, Slider, Stack, TextField, Typography} from "@mui/material";
import ImageUploadField from "crqlar-react-common/dist/components/ImageUploadField";
import NewColorPicker from "crqlar-react-common/dist/components/NewColorPicker";
import {Media} from "crqlar-react-common/dist/models/common/media.model";
import {
    SurveyBannerVariant,
    SurveyContentAlignment,
    SurveyCornerStyle,
    SurveyProgressStyle,
    SurveyQuestionStyle,
    SurveyTheme
} from "crqlar-react-common/dist/models/survey/survey.model";
import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {surveyTextColors} from "../runner/surveyRunnerTheme";
import {PanelField, PanelSection} from "./SurveyPanelSection";
import SurveySidebarPanel from "./SurveySidebarPanel";

interface Props {
    theme?: SurveyTheme
    onThemeChange: (theme: SurveyTheme) => void
    disabled?: boolean
}

const COLOR_FIELDS: {field: keyof SurveyTheme, labelKey: string, hintKey: string}[] = [
    {field: 'primaryColor', labelKey: 'Primary accent', hintKey: 'Actions and active responses'},
    {field: 'secondaryColor', labelKey: 'Secondary accent', hintKey: 'Supporting accent color'},
    {field: 'backgroundColor', labelKey: 'Form background', hintKey: 'The canvas behind the form'},
    {field: 'surfaceColor', labelKey: 'Surface', hintKey: 'Question cards and input surfaces'},
    {field: 'textColor', labelKey: 'Text primary', hintKey: 'Headings, questions and main text'},
    {field: 'textSecondaryColor', labelKey: 'Text secondary', hintKey: 'Descriptions, hints and placeholders'},
];

const MIN_BACKGROUND_OPACITY = 0.05;

const BACKGROUND_OPACITY_STEP = 0.05;

function SurveyDesignPanel({theme, onThemeChange, disabled}: Props) {
    const {t} = useTranslation();

    const patch = (partial: Partial<SurveyTheme>) => onThemeChange({...theme, ...partial});
    // The runner moves a chosen text colour that does not read on the surface; say so under the field.
    const {adjusted} = useMemo(() => surveyTextColors(theme), [theme]);

    return (
        <SurveySidebarPanel>
            <Stack gap={3} divider={<Divider/>} sx={{pt: 3}}>
                <PanelSection title={t('Layout')}>
                    <PanelField
                        label={t('Content alignment')}
                        hint={t('Position the form content at the top, center or bottom of the page')}
                    >
                        <TextField
                            select
                            size={'small'}
                            fullWidth
                            disabled={disabled}
                            value={theme?.contentAlignment ?? SurveyContentAlignment.CENTER}
                            onChange={(event) => patch({
                                contentAlignment: event.target.value as SurveyContentAlignment
                            })}
                        >
                            <MenuItem value={SurveyContentAlignment.TOP}>{t('Top')}</MenuItem>
                            <MenuItem value={SurveyContentAlignment.CENTER}>{t('Center')}</MenuItem>
                            <MenuItem value={SurveyContentAlignment.BOTTOM}>{t('Bottom')}</MenuItem>
                        </TextField>
                    </PanelField>

                    <PanelField
                        label={t('Corner style')}
                        hint={t('Applied to cards, fields and buttons')}
                    >
                        <TextField
                            select
                            size={'small'}
                            fullWidth
                            disabled={disabled}
                            value={theme?.cornerStyle ?? SurveyCornerStyle.SOFT}
                            onChange={(event) => patch({cornerStyle: event.target.value as SurveyCornerStyle})}
                        >
                            <MenuItem value={SurveyCornerStyle.SQUARE}>{t('Square')}</MenuItem>
                            <MenuItem value={SurveyCornerStyle.SOFT}>{t('Soft')}</MenuItem>
                            <MenuItem value={SurveyCornerStyle.ROUNDED}>{t('Rounded')}</MenuItem>
                        </TextField>
                    </PanelField>

                    <PanelField
                        label={t('Question style')}
                        hint={t('Each question on a card, or straight on the page')}
                    >
                        <TextField
                            select
                            size={'small'}
                            fullWidth
                            disabled={disabled}
                            value={theme?.questionStyle ?? SurveyQuestionStyle.CARDS}
                            onChange={(event) => patch({questionStyle: event.target.value as SurveyQuestionStyle})}
                        >
                            <MenuItem value={SurveyQuestionStyle.CARDS}>{t('Cards')}</MenuItem>
                            <MenuItem value={SurveyQuestionStyle.PLAIN}>{t('Plain')}</MenuItem>
                        </TextField>
                    </PanelField>

                    <PanelField
                        label={t('Progress')}
                        hint={t('One segment per page, or one continuous bar')}
                    >
                        <TextField
                            select
                            size={'small'}
                            fullWidth
                            disabled={disabled}
                            value={theme?.progressStyle ?? SurveyProgressStyle.SEGMENTS}
                            onChange={(event) => patch({progressStyle: event.target.value as SurveyProgressStyle})}
                        >
                            <MenuItem value={SurveyProgressStyle.SEGMENTS}>{t('Segments')}</MenuItem>
                            <MenuItem value={SurveyProgressStyle.BAR}>{t('Bar')}</MenuItem>
                        </TextField>
                    </PanelField>
                </PanelSection>

                <PanelSection title={t('Colors')}>
                    {COLOR_FIELDS.map(({field, labelKey, hintKey}) => (
                        <PanelField key={field} label={t(labelKey)} hint={t(hintKey)}>
                            <NewColorPicker
                                id={field}
                                value={theme?.[field] as string | undefined}
                                disabled={disabled}
                                onChange={(color) => patch({[field]: color} as Partial<SurveyTheme>)}
                            />
                            {(field === 'textColor' || field === 'textSecondaryColor') && adjusted[field] && (
                                <Typography variant={'body2'} color={'text.secondary'} sx={{mt: 0.5}}>
                                    {t('Hard to read on the surface, so guests see an adjusted shade.')}
                                </Typography>
                            )}
                        </PanelField>
                    ))}
                </PanelSection>

                <PanelSection title={t('Images')}>
                    <PanelField
                        label={t('Banner image')}
                        hint={t('A wide image strip shown at the very top of the form')}
                    >
                        <ImageUploadField
                            id={'banner-media'}
                            image={theme?.bannerMedia}
                            setImage={(media?: Media) => patch({bannerMedia: media})}
                            disabled={disabled}
                            width={'100%'}
                            height={180}
                        />
                    </PanelField>

                    {!!theme?.bannerMedia && (
                        <PanelField
                            label={t('Banner width')}
                            hint={t('Contained keeps the banner inside the form column, full width spans the whole page')}
                        >
                            <TextField
                                select
                                size={'small'}
                                fullWidth
                                disabled={disabled}
                                value={theme?.bannerVariant ?? SurveyBannerVariant.CONTAINED}
                                onChange={(event) => patch({
                                    bannerVariant: event.target.value as SurveyBannerVariant
                                })}
                            >
                                <MenuItem value={SurveyBannerVariant.CONTAINED}>{t('Contained')}</MenuItem>
                                <MenuItem value={SurveyBannerVariant.FULL_WIDTH}>{t('Full width')}</MenuItem>
                            </TextField>
                        </PanelField>
                    )}

                    <PanelField
                        label={t('Logo')}
                        hint={t('Shown centered above the form content')}
                    >
                        <ImageUploadField
                            id={'logo-media'}
                            image={theme?.logoMedia}
                            setImage={(media?: Media) => patch({logoMedia: media})}
                            disabled={disabled}
                            supportSvg
                            width={'100%'}
                            height={180}
                        />
                    </PanelField>

                    <PanelField
                        label={t('Background image')}
                        hint={t('Covers the entire page behind the form')}
                    >
                        <ImageUploadField
                            id={'background-media'}
                            image={theme?.backgroundMedia}
                            setImage={(media?: Media) => patch({backgroundMedia: media})}
                            disabled={disabled}
                            width={'100%'}
                            height={180}
                        />
                    </PanelField>

                    {!!theme?.backgroundMedia && (
                        <PanelField
                            label={t('Background opacity')}
                            hint={t('How strongly the image shows through the background color')}
                        >
                            <Slider
                                size={'small'}
                                min={MIN_BACKGROUND_OPACITY}
                                max={1}
                                step={BACKGROUND_OPACITY_STEP}
                                valueLabelDisplay={'auto'}
                                disabled={disabled}
                                value={theme?.backgroundOpacity ?? 1}
                                onChange={(_event, value) => patch({backgroundOpacity: value as number})}
                            />
                        </PanelField>
                    )}
                </PanelSection>
            </Stack>
        </SurveySidebarPanel>
    );
}

export default SurveyDesignPanel;
